import React, { useState } from 'react';
import { FaFilter, FaDownload, FaEye, FaTrash, FaCalendarAlt, FaBuilding, FaFileAlt, FaChartBar, FaUpload, FaCrown, FaSearch, FaFilePdf, FaCheckCircle, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { FiRefreshCw } from 'react-icons/fi';
import UploadContent from '../UploadContent/index';

const SuperAdminExtractionsContent = ({ onExtractionComplete }) => {
  const [filters, setFilters] = useState({
    company: 'all',
    reportType: 'all',
    fromDate: '',
    toDate: ''
  });

  // New state for showing upload content
  const [showUploadContent, setShowUploadContent] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [extractions, setExtractions] = useState([
    {
      id: 1,
      company: 'United Company BUSAL International RISC',
      title: 'Income Statement, annual_combined',
      reportType: 'annual',
      created: 'Jan 8, 2025, 10:27 AM',
      status: 'completed',
      size: '2.4 MB',
      pages: 45,
      processedBy: 'Super Admin'
    },
    {
      id: 2,
      company: 'United Company BUSAL International RISC',
      title: 'Income Statement',
      reportType: 'annual',
      created: 'Jan 8, 2025, 10:27 AM',
      status: 'completed',
      size: '1.8 MB',
      pages: 32,
      processedBy: 'Super Admin'
    },
    {
      id: 3,
      company: 'United Company BUSAL International RISC',
      title: 'Balance Sheet, annual_combined',
      reportType: 'annual',
      created: 'Jan 8, 2025, 10:24 AM',
      status: 'completed',
      size: '3.1 MB',
      pages: 56,
      processedBy: 'System'
    },
    {
      id: 4,
      company: 'United Company BUSAL International RISC',
      title: 'Balance Sheet',
      reportType: 'annual',
      created: 'Jan 8, 2025, 10:24 AM',
      status: 'completed',
      size: '2.2 MB',
      pages: 38,
      processedBy: 'Super Admin'
    },
    {
      id: 5,
      company: 'Tradify ITCS Group Tradify Bank JSC',
      title: 'Balance Sheet, annual_combined',
      reportType: 'annual',
      created: 'Sep 30, 2025, 04:54 PM',
      status: 'completed',
      size: '4.2 MB',
      pages: 67,
      processedBy: 'System'
    },
    {
      id: 6,
      company: 'Abu Dhabi National Energy Company',
      title: 'Quarterly Financial Report',
      reportType: 'quarterly',
      created: 'Feb 15, 2025, 09:15 AM',
      status: 'processing',
      size: '1.5 MB',
      pages: 28,
      processedBy: 'Super Admin'
    },
    {
      id: 7,
      company: 'Georgia Capital PLC',
      title: 'Cash Flow Statement',
      reportType: 'annual',
      created: 'Feb 14, 2025, 03:45 PM',
      status: 'failed',
      size: '0 MB',
      pages: 0,
      processedBy: 'System'
    }
  ]);

  const stats = [
    { 
      label: 'Total Extractions', 
      value: '367', 
      subtext: 'Record (7 days)', 
      icon: <FaFileAlt />, 
      color: 'bg-gradient-to-r from-blue-500 to-blue-600',
      trend: '+12%'
    },
    { 
      label: 'Annual Reports', 
      value: '365', 
      subtext: 'Last 30 days', 
      icon: <FaChartBar />, 
      color: 'bg-gradient-to-r from-green-500 to-green-600',
      trend: '+8%'
    },
    { 
      label: 'Quarterly Reports', 
      value: '2', 
      subtext: 'Last 30 days', 
      icon: <FiRefreshCw />, 
      color: 'bg-gradient-to-r from-purple-500 to-purple-600',
      trend: '+15%'
    },
    { 
      label: 'Processing', 
      value: '1', 
      subtext: 'Active now', 
      icon: <FaSpinner className="animate-spin" />, 
      color: 'bg-gradient-to-r from-orange-500 to-orange-600',
      trend: ''
    }
  ];

  const companies = [
    'All Companies',
    'United Company BUSAL International RISC',
    'Tradify ITCS Group Tradify Bank JSC',
    'Abu Dhabi National Energy Company',
    'Georgia Capital PLC'
  ];

  const reportTypes = [
    'All Types',
    'Annual',
    'Quarterly',
    'Monthly',
    'Income Statement',
    'Balance Sheet',
    'Cash Flow'
  ];

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyFilters = () => {
    console.log('Applying filters:', filters);
    // In a real app, this would filter the extractions
  };

  const handleDownload = (id) => {
    console.log('Downloading extraction:', id);
    alert(`Downloading extraction ${id}`);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this extraction?')) {
      setExtractions(extractions.filter(ext => ext.id !== id));
    }
  };

  // Handle extraction completion
  const handleExtractionComplete = (extractedData, filesToExtract, sections) => {
    console.log('Extraction completed:', extractedData);
    
    // Add new extraction to the list
    const newExtraction = {
      id: extractions.length + 1,
      company: extractedData.companyName,
      title: sections.map(s => s.title).join(', '),
      reportType: extractedData.periodType,
      created: new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      status: 'completed',
      size: '1.2 MB',
      pages: 24,
      processedBy: 'Super Admin'
    };
    
    setExtractions(prev => [newExtraction, ...prev]);
    
    // Close the upload content view and return to main view
    setShowUploadContent(false);
    
    if (onExtractionComplete) {
      onExtractionComplete(extractedData, filesToExtract, sections);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 dark:from-green-900/30 dark:to-green-800/20 dark:text-green-300 border border-green-200 dark:border-green-800',
      processing: 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 dark:from-yellow-900/30 dark:to-yellow-800/20 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800',
      failed: 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 dark:from-red-900/30 dark:to-red-800/20 dark:text-red-300 border border-red-200 dark:border-red-800'
    };
    
    const icons = {
      completed: <FaCheckCircle className="w-3 h-3" />,
      processing: <FaSpinner className="w-3 h-3 animate-spin" />,
      failed: <FaExclamationTriangle className="w-3 h-3" />
    };
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getReportTypeBadge = (type) => {
    const styles = {
      annual: 'bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 dark:from-blue-900/30 dark:to-blue-800/20 dark:text-blue-300 border border-blue-200 dark:border-blue-800',
      quarterly: 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-800 dark:from-purple-900/30 dark:to-purple-800/20 dark:text-purple-300 border border-purple-200 dark:border-purple-800',
      monthly: 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 dark:from-gray-800 dark:to-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
    };
    return (
      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${styles[type] || styles.annual}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  // Filter extractions based on search
  const filteredExtractions = extractions.filter(extraction => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      extraction.company.toLowerCase().includes(searchLower) ||
      extraction.title.toLowerCase().includes(searchLower) ||
      extraction.reportType.toLowerCase().includes(searchLower) ||
      extraction.status.toLowerCase().includes(searchLower)
    );
  });

  // If showUploadContent is true, render the UploadContent component
  if (showUploadContent) {
    return (
      <div className="space-y-6 animate-fadeIn">
        {/* Back button and header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowUploadContent(false)}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 
                         bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 
                         rounded-lg hover:from-gray-200 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:to-gray-600 
                         transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-700"
            >
              ← Back to Extractions
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Document Processing</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Upload and extract data from financial documents
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full">
            <FaCrown className="text-white text-sm" />
            <span className="text-xs font-medium text-white">Super Admin</span>
          </div>
        </div>
        
        {/* Upload Content Component */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="p-6">
            <UploadContent onExtractionComplete={handleExtractionComplete} />
          </div>
        </div>
      </div>
    );
  }

  // Original view - Extraction management dashboard
  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header with Extraction button */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg shadow-lg">
              <FaUpload className="text-white text-lg" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Extraction Management</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Manage and monitor all document extractions across the system
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search extractions..."
              className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg 
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white 
                       focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowUploadContent(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 
                     text-white rounded-lg transition-all duration-200 flex items-center gap-2 font-medium 
                     shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <FaUpload />
            New Extraction
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-800 group hover:border-purple-300 dark:hover:border-purple-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{stat.label}</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                  {stat.trend && (
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      {stat.trend}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{stat.subtext}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-xl text-white shadow-md`}>
                {stat.icon}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
              <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-1.5">
                <div 
                  className="h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-500"
                  style={{ width: index === 3 ? '45%' : '85%' }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters Section */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-2 mb-6">
          <div className="p-2 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-lg">
            <FaFilter className="text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filter Extractions</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <div className="flex items-center gap-2">
                <FaBuilding className="text-gray-400" />
                Company Name
              </div>
            </label>
            <select
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg 
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                         transition-colors"
              value={filters.company}
              onChange={(e) => handleFilterChange('company', e.target.value)}
            >
              {companies.map((company, index) => (
                <option key={index} value={company === 'All Companies' ? 'all' : company}>
                  {company}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <div className="flex items-center gap-2">
                <FaFileAlt className="text-gray-400" />
                Report Type
              </div>
            </label>
            <select
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg 
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                         transition-colors"
              value={filters.reportType}
              onChange={(e) => handleFilterChange('reportType', e.target.value)}
            >
              {reportTypes.map((type, index) => (
                <option key={index} value={type === 'All Types' ? 'all' : type.toLowerCase()}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <div className="flex items-center gap-2">
                <FaCalendarAlt className="text-gray-400" />
                From Date
              </div>
            </label>
            <input
              type="date"
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg 
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                         transition-colors"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <div className="flex items-center gap-2">
                <FaCalendarAlt className="text-gray-400" />
                To Date
              </div>
            </label>
            <input
              type="date"
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg 
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                         transition-colors"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setFilters({
                company: 'all',
                reportType: 'all',
                fromDate: '',
                toDate: ''
              });
              setSearchTerm('');
            }}
            className="px-5 py-2.5 text-gray-700 dark:text-gray-300 
                       bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 
                       hover:from-gray-200 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:to-gray-600
                       rounded-lg transition-all duration-200 font-medium border border-gray-300 dark:border-gray-700"
          >
            Clear Filters
          </button>
          <button
            onClick={applyFilters}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 
                     text-white rounded-lg transition-all duration-200 flex items-center gap-2 font-medium shadow-md hover:shadow-lg"
          >
            <FaFilter />
            Apply Filters
          </button>
        </div>
      </div>

      {/* Extractions Table */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-800">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Extraction Records</h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Showing {filteredExtractions.length} of {extractions.length} records
            </span>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  COMPANY
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  TITLE
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  TYPE
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  CREATED
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  STATUS
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  PROCESSED BY
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {filteredExtractions.map((extraction) => (
                <tr key={extraction.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="p-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg mr-3">
                        <FaBuilding className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {extraction.company}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FaFilePdf className="text-red-500 mr-2" />
                      <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                        {extraction.title}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getReportTypeBadge(extraction.reportType)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-white">{extraction.created}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-1">
                      <span>{extraction.size}</span>
                      <span>•</span>
                      <span>{extraction.pages} pages</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(extraction.status)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {extraction.processedBy === 'Super Admin' ? (
                        <>
                          <FaCrown className="text-yellow-500 mr-2" />
                          <span className="text-sm text-gray-900 dark:text-white font-medium">
                            {extraction.processedBy}
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {extraction.processedBy}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDownload(extraction.id)}
                        className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 
                                 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Download"
                      >
                        <FaDownload />
                      </button>
                      <button
                        className="p-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 
                                 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title="View"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleDelete(extraction.id)}
                        className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 
                                 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredExtractions.length}</span> of{' '}
              <span className="font-medium">367</span> extractions
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 
                         border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled
              >
                Previous
              </button>
              {[1, 2, 3, 4, 5].map((page) => (
                <button
                  key={page}
                  className={`px-3.5 py-1.5 rounded-lg font-medium transition-all duration-200 ${
                    page === 1
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                className="px-3 py-1.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 
                         border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 
                         transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
      
   
      
    </div>
  );
};

export default SuperAdminExtractionsContent;